import { Controller } from "@hotwired/stimulus"
import { PreviewMessageService } from "../services/preview_message_service"

// Live preview for the music-promo-short wizard. Pushes ConfigBuilder props into
// the Remotion @remotion/player iframe via postMessage, and re-fetches props
// (with the wizard form's UNSAVED values as transient overrides) on every edit so
// the preview tracks the look without saving.
//
// Markup (preview_panel_component):
//   <div data-controller="music-promo-preview"
//        data-music-promo-preview-origin-value="http://localhost:3005"
//        data-music-promo-preview-props-url-value="/.../preview_props"
//        data-music-promo-preview-props-value="{…}">
//     <iframe data-music-promo-preview-target="iframe" src="…/preview/music-promo-short?token=…">
//   </div>
const DEBOUNCE_MS = 400
const FIELD_PREFIX = "video_config[config_data][music_promo_short]"

export default class extends Controller {
  static targets = ["iframe"]
  static values = { origin: String, propsUrl: String, props: Object }

  connect() {
    this.service = new PreviewMessageService(this.iframeTarget, this.originValue)
    this.debounceTimer = null
    this.onEdit = this.onEdit.bind(this)

    // Push the saved-config props once the player is ready (and shortly after, in
    // case the load event already fired).
    this.iframeTarget.addEventListener("load", () => this.send(this.propsValue))
    this.sendTimer = setTimeout(() => this.send(this.propsValue), DEBOUNCE_MS)

    document.addEventListener("input", this.onEdit)
    document.addEventListener("change", this.onEdit)
  }

  disconnect() {
    document.removeEventListener("input", this.onEdit)
    document.removeEventListener("change", this.onEdit)
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    if (this.sendTimer) clearTimeout(this.sendTimer)
  }

  onEdit(event) {
    const name = event.target && event.target.name
    if (!name || !name.startsWith(FIELD_PREFIX)) return
    this.scheduleRefresh()
  }

  scheduleRefresh() {
    if (this.debounceTimer) clearTimeout(this.debounceTimer)
    this.debounceTimer = setTimeout(() => this.refresh(), DEBOUNCE_MS)
  }

  async refresh() {
    const form = document.querySelector("[data-preview-form]")
    if (!form) return
    const body = new FormData(form)
    body.delete("_method") // POST to preview_props, don't let Rails see PATCH
    try {
      const res = await fetch(this.propsUrlValue, {
        method: "POST",
        body,
        headers: { "X-CSRF-Token": this.csrfToken(), Accept: "application/json" },
      })
      if (!res.ok) return
      this.send(await res.json())
    } catch (_error) {
      // Preview is best-effort — never disrupt the wizard on a fetch hiccup.
    }
  }

  send(props) {
    if (props && typeof props === "object" && Object.keys(props).length > 0) {
      this.service.setProps(props)
    }
  }

  csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }
}
